import { useEffect, useRef, useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { VERSION_LABEL } from '../../utils/appPrefs'
import {
  Gamepad2, MessageCircle, Newspaper, FileText, Mic, Globe, Headphones,
  CalendarDays, Calendar, Image, CreditCard, Settings, Info, X, ChevronRight, Timer
} from 'lucide-react'

const GROUPS = [
  {
    title: 'Écouter et découvrir',
    items: [
      { icon: Timer,        label: 'Réveil et minuteur', path: '/tools' },
      { icon: Mic,          label: 'Podcasts',          path: '/podcasts' },
      { icon: Headphones,   label: 'Où nous écouter',   path: '/platforms' },
      { icon: CalendarDays, label: 'Planning',          path: '/planning' },
      { icon: Newspaper,    label: 'Blog',              path: '/blog' },
      { icon: FileText,     label: 'Presse',            path: '/presse' },
      { icon: Gamepad2,     label: 'Jeux',              path: '/game' }
    ]
  },
  {
    title: 'Communauté',
    items: [
      { icon: Globe,        label: 'Notre univers',     path: '/notre-univers', badge: 'Nouveau' },
      { icon: Calendar,     label: 'Événements',        path: '/events' },
      { icon: Image,        label: 'Galerie',           path: '/gallery' },
      { icon: CreditCard,   label: 'Adhésion',          path: '/abonnement' },
      { icon: MessageCircle,label: 'Contact',           path: '/contact' }
    ]
  },
  {
    title: "L'application",
    items: [
      { icon: Settings,     label: 'Paramètres',        path: '/settings' },
      { icon: Info,         label: 'À propos',          path: '/about' }
    ]
  }
]

/**
 * Feuille "Plus" qui monte depuis le bas.
 * Fermeture : bouton, tap sur le fond, touche Échap, ou glisser vers le bas.
 */
function MoreSheet({ open, onClose, currentUser }) {
  const { pathname } = useLocation()
  const sheetRef = useRef(null)
  const dragRef = useRef({ startY: 0, dy: 0, active: false })
  const [dragging, setDragging] = useState(false)

  // Fermer quand on change de page
  useEffect(() => { if (open) onClose?.() }, [pathname]) // eslint-disable-line react-hooks/exhaustive-deps

  // Échap + blocage du défilement de la page derrière
  useEffect(() => {
    if (!open) return undefined
    const onKey = (e) => { if (e.key === 'Escape') onClose?.() }
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    window.addEventListener('keydown', onKey)
    return () => {
      document.body.style.overflow = prev
      window.removeEventListener('keydown', onKey)
    }
  }, [open, onClose])

  const setOffset = (y) => {
    if (sheetRef.current) sheetRef.current.style.transform = y ? `translateY(${y}px)` : ''
  }

  const onTouchStart = (e) => {
    dragRef.current = { startY: e.touches[0].clientY, dy: 0, active: true }
    setDragging(true)
  }

  const onTouchMove = (e) => {
    if (!dragRef.current.active) return
    const dy = Math.max(0, e.touches[0].clientY - dragRef.current.startY)
    dragRef.current.dy = dy
    setOffset(dy)
  }

  const onTouchEnd = () => {
    const { dy } = dragRef.current
    dragRef.current.active = false
    setDragging(false)
    setOffset(0)
    if (dy > 90) onClose?.()
  }

  const name = currentUser?.displayName || currentUser?.email?.split('@')[0]

  return (
    <>
      <div
        className={`j-sheet-backdrop ${open ? 'is-open' : ''}`}
        onClick={onClose}
        aria-hidden="true"
      />

      <section
        ref={sheetRef}
        className={`j-sheet ${open ? 'is-open' : ''} ${dragging ? 'is-dragging' : ''}`}
        role="dialog"
        aria-modal="true"
        aria-label="Toutes les rubriques"
        aria-hidden={!open}
        inert={open ? undefined : ''}
      >
        <div
          className="j-sheet__grab"
          onTouchStart={onTouchStart}
          onTouchMove={onTouchMove}
          onTouchEnd={onTouchEnd}
        >
          <div className="j-sheet__handle" />
          <div className="j-sheet__head">
            <h2 className="j-sheet__title">Toutes les rubriques</h2>
            <button type="button" className="j-icon-btn" onClick={onClose} aria-label="Fermer">
              <X size={22} />
            </button>
          </div>
        </div>

        <div className="j-sheet__body">
          <Link to="/profile" className="j-user">
            <div className="j-user__avatar">
              {currentUser?.photoURL
                ? <img src={currentUser.photoURL} alt="" />
                : (name ? name.charAt(0).toUpperCase() : '♪')}
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div className="j-user__name" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {name || 'Se connecter'}
              </div>
              <div className="j-user__hint">
                {currentUser ? 'Voir mon profil et mes badges' : 'Pour chatter, voter et envoyer des dédicaces'}
              </div>
            </div>
            <ChevronRight size={20} color="var(--j-muted)" />
          </Link>

          {GROUPS.map((group) => (
            <div key={group.title} className="j-group">
              <h3 className="j-group__title">{group.title}</h3>
              <div className="j-tiles">
                {group.items.map(({ icon: Icon, label, path, badge }) => (
                  <Link
                    key={path}
                    to={path}
                    className={`j-tile ${pathname === path ? 'is-active' : ''}`}
                  >
                    {badge && <span className="j-tile__badge">{badge}</span>}
                    <span className="j-tile__icon"><Icon size={22} /></span>
                    {label}
                  </Link>
                ))}
              </div>
            </div>
          ))}

          <div className="j-sheet__foot">Radio 100% Johnny. {VERSION_LABEL}</div>
        </div>
      </section>
    </>
  )
}

export default MoreSheet
